import { supabase } from "@/lib/supabase";

export interface ProductRow {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  featured: boolean;
  archivedAt: string | null;
  categoryName: string | null;
  brandName: string | null;
  variantCount: number;
  minPrice: string | null;
  maxPrice: string | null;
}

export type CatalogFilter = "all" | "active" | "inactive" | "featured" | "archived";

export async function fetchProducts({
  organizationId,
  filter,
  search,
  page,
  pageSize,
}: {
  organizationId: string;
  filter: CatalogFilter;
  search?: string;
  page: number;
  pageSize: number;
}): Promise<{ rows: ProductRow[]; total: number }> {
  let query = supabase
    .from("products")
    .select("id, name, slug, active, featured, archived_at, category_id, brand_id", {
      count: "exact",
    })
    .eq("organization_id", organizationId)
    .order("name")
    .range(page * pageSize, page * pageSize + pageSize - 1);

  // Lo archivado se esconde de todas las vistas menos de la suya. Sin esa
  // vista, archivar seria un viaje de ida: el producto desaparece del panel y
  // no hay forma de volver a el salvo escribiendo la URL a mano.
  query =
    filter === "archived"
      ? query.not("archived_at", "is", null)
      : query.is("archived_at", null);

  if (filter === "active") query = query.eq("active", true);
  if (filter === "inactive") query = query.eq("active", false);
  if (filter === "featured") query = query.eq("featured", true);

  const term = search?.trim();
  if (term) {
    query = query.ilike("name", `%${term.replace(/[%,]/g, "")}%`);
  }

  const { data, error, count } = await query;
  if (error) throw error;

  const products = data ?? [];
  if (products.length === 0) return { rows: [], total: count ?? 0 };

  // Consultas separadas en lugar de selects anidados: ver el comentario de
  // features/tenancy/api.ts sobre la inferencia de tipos.
  const ids = products.map((p) => p.id);
  const [variants, categories, brands] = await Promise.all([
    supabase.from("product_variants").select("product_id, sale_price, active").in("product_id", ids),
    supabase.from("categories").select("id, name").eq("organization_id", organizationId),
    supabase.from("brands").select("id, name").eq("organization_id", organizationId),
  ]);

  const catBy = new Map((categories.data ?? []).map((c) => [c.id, c.name]));
  const brandBy = new Map((brands.data ?? []).map((b) => [b.id, b.name]));

  const byProduct = new Map<string, number[]>();
  for (const v of variants.data ?? []) {
    byProduct.set(v.product_id, [...(byProduct.get(v.product_id) ?? []), Number(v.sale_price)]);
  }

  const rows = products.map((p) => {
    const prices = (byProduct.get(p.id) ?? []).sort((a, b) => a - b);
    return {
      id: p.id,
      name: p.name,
      slug: p.slug,
      active: p.active,
      featured: p.featured,
      archivedAt: p.archived_at,
      categoryName: p.category_id ? (catBy.get(p.category_id) ?? null) : null,
      brandName: p.brand_id ? (brandBy.get(p.brand_id) ?? null) : null,
      variantCount: prices.length,
      minPrice: prices.length > 0 ? String(prices[0]) : null,
      maxPrice: prices.length > 0 ? String(prices[prices.length - 1]) : null,
    } satisfies ProductRow;
  });

  return { rows, total: count ?? 0 };
}

export async function fetchCustomers({
  organizationId,
  search,
  page,
  pageSize,
}: {
  organizationId: string;
  search?: string;
  page: number;
  pageSize: number;
}) {
  let query = supabase
    .from("customers")
    .select("id, first_name, last_name, phone, email, created_at", { count: "exact" })
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  const term = search?.trim();
  if (term) {
    const escaped = term.replace(/[%,]/g, "");
    query = query.or(
      `first_name.ilike.%${escaped}%,last_name.ilike.%${escaped}%,phone.ilike.%${escaped}%`,
    );
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { rows: data ?? [], total: count ?? 0 };
}
